import { useState, useEffect, useRef, useCallback } from "react";
import { getSupabaseClientSync } from "@/app/_module/services/supabaseClient";
import type { DTMFDigit } from "@/app/_module/services/dtmfService";
import { removeRealtimeChannel } from "../services/realtimeTeardown";
import { useRealtimeRejoin } from "@/app/_module/services/realtimeHealth";

interface UseDTMFRealtimeOptions {
  callSid: string | null;
  enabled?: boolean;
  userId: string | null;
}

/**
 * Hook to receive DTMF digits via Supabase Realtime push (replaces useDTMFPolling).
 *
 * Subscribes to the dedicated DTMF broadcast channel `incoming_calls:{userId}:dtmf`
 * to avoid collisions with other Supabase Realtime subscriptions (e.g. IncomingCallContext
 * on the base channel, MultiLineDialer on :multiline). The backend broadcasts DTMF events
 * to all channel suffixes including :dtmf via `broadcastToAllDialerChannels`.
 *
 * Listens for three events:
 *   - `dtmf_digit`        — a new digit pressed/spoken by the customer
 *   - `speech_processing` — speech-to-digit conversion started/stopped
 *   - `dtmf_cleared`      — backend cleared the digit buffer
 *
 * Events are filtered by `parentCallSid` so only digits for the current call
 * are accumulated.
 *
 * @returns Same shape as useDTMFPolling for backward-compatible swap.
 */
export function useDTMFRealtime({
  callSid,
  enabled = true,
  userId,
}: UseDTMFRealtimeOptions) {
  const [digits, setDigits] = useState<DTMFDigit[]>([]);
  const [isSpeechProcessing, setIsSpeechProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Bumped by the subscribe status handler when the channel dies, so the effect re-runs
  // and re-subscribes. See realtimeHealth.ts for why this exists.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const channelRef = useRef<any>(null);
  const callSidRef = useRef(callSid);

  // Keep callSid ref in sync so broadcast handlers always see the latest value
  // Guarded rejoin: backoff + attempt cap (an unguarded retry loop hangs the tab).
  const { rejoinToken, handleStatus } = useRealtimeRejoin("dtmf");
  useEffect(() => {
    callSidRef.current = callSid;
  }, [callSid]);

  // Clear digits — resets local state only.
  // The backend buffer is cleared separately by the collect-sensitive flow.
  const clearDigits = useCallback(() => {
    setDigits([]);
  }, []);

  useEffect(() => {
    // Reset state when callSid changes
    setDigits([]);
    setError(null);
    setIsSpeechProcessing(false);

    // Don't subscribe if disabled, missing userId or no callSid
    if (!enabled || !userId || !callSid) {
      return;
    }

    const supabase = getSupabaseClientSync();
    if (!supabase) {
      setError("Supabase client not available");
      return;
    }

    // IMPORTANT: Use dedicated :dtmf suffix to avoid channel collisions.
    // Other components subscribe to the same userId's channels:
    //   - IncomingCallContext → incoming_calls:{userId} (base)
    //   - MultiLineDialer → incoming_calls:{userId}:multiline
    //   - LeadDialer → incoming_calls:{userId}:leaddialer
    // If we shared any of those channel names, unsubscribing here would kill
    // the other component's subscription (Supabase sends phx_leave per topic).
    const channelName = `incoming_calls:${userId}:dtmf`;
    const channel = supabase.channel(channelName);

    channel.on("broadcast", { event: "dtmf_digit" }, (message: { payload?: Record<string, unknown> }) => {
      const payload = message?.payload;
      if (!payload || payload.parentCallSid !== callSidRef.current) return;

      const digitStr = (payload.digit as string) || "";
      const now = new Date();
      const duration = (payload.callDuration as number) || undefined;

      // Backend may send multiple digits as a single string (e.g. "123456789")
      // to preserve ordering. Split into individual DTMFDigit entries.
      const newDigits: DTMFDigit[] = digitStr.split("").map((d) => ({
        digit: d,
        timestamp: now,
        callDuration: duration,
      }));
      setDigits((prev) => [...prev, ...newDigits]);
    });

    channel.on("broadcast", { event: "speech_processing" }, (message: { payload?: Record<string, unknown> }) => {
      const payload = message?.payload;
      if (!payload || payload.parentCallSid !== callSidRef.current) return;
      setIsSpeechProcessing(!!payload.isSpeechProcessing);
    });

    channel.on("broadcast", { event: "dtmf_cleared" }, (message: { payload?: Record<string, unknown> }) => {
      const payload = message?.payload;
      if (!payload || payload.parentCallSid !== callSidRef.current) return;
      setDigits([]);
    });

    channel.subscribe((status: string, err?: Error) => {
      if (status === "SUBSCRIBED") setError(null);
      else if (status === "CHANNEL_ERROR" || status === "TIMED_OUT" || status === "CLOSED") {
        setError(`Realtime channel ${status.toLowerCase()} — reconnecting`);
      }
      // Backoff + cap live in the hook; this callback must never re-subscribe directly.
      handleStatus(status, err);
    });

    channelRef.current = channel;

    return () => {
      removeRealtimeChannel(channel);
      channelRef.current = null;
    };
    // rejoinToken is a dependency so a dead channel re-subscribes: the status handler
    // above bumps it, this effect tears the old channel down and builds a fresh one.
  }, [callSid, enabled, userId, rejoinToken]);

  return {
    digits,
    isPolling: enabled && !!callSid, // backward compat field name
    isSpeechProcessing,
    error,
    digitCount: digits.length,
    clearDigits,
  };
}
